/**
 * StrengthTrends — small section embedded on the Progress page.
 *
 * Lets the user:
 *  - See their top PRs (sorted by estimated 1RM)
 *  - Pick any exercise from a dropdown → render a line chart of estimated
 *    1RM over time (Epley) using recharts
 *
 * Skips rendering entirely if the user has no logs yet.
 */
import { useEffect, useMemo, useState } from 'react';
import { Trophy, TrendingUp, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { lbToUnit } from './units';

export default function StrengthTrends() {
  const { api, user } = useAuth();
  const unit = user?.weight_unit === 'kg' ? 'kg' : 'lb';
  const [prs, setPrs] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prRes, exRes] = await Promise.all([
          api().get('/strength/prs'),
          api().get('/strength/exercises'),
        ]);
        if (cancelled) return;
        setPrs(prRes.data?.prs || []);
        setExercises(exRes.data?.exercises || []);
        if ((exRes.data?.exercises || []).length) {
          setSelected(exRes.data.exercises[0].name);
        }
      } catch {
        /* user has no logs yet — render nothing */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => {
    if (!selected) return undefined;
    let cancelled = false;
    setChartLoading(true);
    (async () => {
      try {
        const res = await api().get(`/strength/history/${encodeURIComponent(selected)}`);
        if (cancelled) return;
        setHistory(res.data?.points || []);
      } catch {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected, api]);

  const chartData = useMemo(() => history.map((p) => ({
    date: new Date(p.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    e1rm: lbToUnit(p.estimated_1rm, unit),
    weight: lbToUnit(p.weight_lbs, unit),
    reps: p.reps,
  })), [history, unit]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 mb-6 flex items-center justify-center min-h-[120px]">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  // No logs yet → coach the user gently. Don't hide entirely, the message
  // converts curious viewers into loggers.
  if (!prs.length && !exercises.length) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-gradient-to-br from-primary/10 via-card to-card border border-primary/30 rounded-2xl p-6 mb-6"
        data-testid="strength-trends-empty"
      >
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-xl text-white uppercase tracking-wider">Strength Tracker</h3>
        </div>
        <p className="text-muted-foreground text-sm">
          Log your weight + reps during your next workout. We'll celebrate every PR
          and chart your progress over time. ✨
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="bg-card border border-border rounded-2xl p-6 mb-6"
      data-testid="strength-trends"
    >
      <div className="flex items-center gap-3 mb-5">
        <TrendingUp className="h-5 w-5 text-primary" />
        <h3 className="font-heading text-xl text-white uppercase tracking-wider">Strength Trends</h3>
      </div>

      {/* PR ribbon */}
      {prs.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2 mb-5">
          {prs.slice(0, 6).map((pr) => (
            <div
              key={pr.exercise_name}
              className="flex-shrink-0 bg-yellow-500/10 border border-yellow-500/30 rounded-xl px-4 py-3 min-w-[140px]"
              data-testid={`pr-card-${pr.exercise_name}`}
            >
              <div className="flex items-center gap-1.5 text-yellow-300 text-[10px] uppercase tracking-wider font-medium mb-1">
                <Trophy className="h-3 w-3" /> PR
              </div>
              <p className="font-heading text-white text-sm leading-tight">{pr.exercise_display}</p>
              <p className="text-yellow-200 font-bold text-lg mt-1">{lbToUnit(pr.weight_lbs, unit)} {unit} × {pr.reps}</p>
              <p className="text-muted-foreground text-[11px]">~ {lbToUnit(pr.estimated_1rm, unit)} {unit} 1RM</p>
            </div>
          ))}
        </div>
      )}

      {/* Exercise picker + chart */}
      {exercises.length > 0 && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Exercise:</span>
            <select
              value={selected || ''}
              onChange={(e) => setSelected(e.target.value)}
              className="bg-black/60 border border-border rounded-lg px-3 py-1.5 text-sm text-white focus:border-primary outline-none"
              data-testid="strength-exercise-picker"
            >
              {exercises.map((ex) => (
                <option key={ex.name} value={ex.name}>{ex.display}</option>
              ))}
            </select>
          </div>

          <div className="h-56 w-full" data-testid="strength-trend-chart">
            {chartLoading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : chartData.length < 2 ? (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Log this exercise in 2+ workouts to see the trend.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis dataKey="date" stroke="#666" fontSize={11} />
                  <YAxis stroke="#666" fontSize={11} domain={unit === 'kg' ? ['dataMin - 2', 'dataMax + 2'] : ['dataMin - 5', 'dataMax + 5']} unit={` ${unit}`} width={56} />
                  <Tooltip
                    contentStyle={{ background: '#0a0a0a', border: '1px solid #FFD700', borderRadius: 12 }}
                    labelStyle={{ color: '#FFD700' }}
                    formatter={(v, _key, item) => {
                      const p = item?.payload;
                      return [`${v} ${unit}`, `Est. 1RM (from ${p?.weight} ${unit} × ${p?.reps})`];
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="e1rm"
                    stroke="#FFD700"
                    strokeWidth={2.5}
                    dot={{ fill: '#FFD700', r: 3 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
}
